import { createStorefrontApiClient } from '@shopify/storefront-api-client';

//https://shopify.dev/docs/api/storefront/latest/objects/cart

const cart_fields = `
id
createdAt
updatedAt
totalQuantity
attributes {
  key
  value
}
checkoutUrl
cost {
    totalAmount {
      amount
      currencyCode
    }
    subtotalAmount {
      amount
      currencyCode
    }
}
lines(first: 250, reverse: true) {
  edges {
    node {
      quantity
      id
      cost {
        totalAmount {
          amount
          currencyCode
        }
      }
      merchandise {
        ... on ProductVariant {
            id
            title
            image {
              id
              url 
            }
            price {
              amount
              currencyCode
            }
            product {
              title
            }
        }
      }
    }
  }
}
`;

const QueryCreateCart = `
  mutation {
    cartCreate(input: {}) {
    cart {${cart_fields}}
    }
  }
`;

const QueryGetCart = `
  query ($Id: ID!) {
    cart(id: $Id) { ${cart_fields} }
}
`;

const QuerySetNote = `
  mutation cartAttributesUpdate(
    $cartId: ID!
    $attributes: [AttributeInput!]!
  ) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ${cart_fields}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const QueryAddItem = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ${cart_fields}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const QueryRemItem = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ${cart_fields}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const QueryUpdateItem = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ${cart_fields}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const QueryAvailable = `
  query ProductQuery($id: ID) {
    product(id: $id) {
      id
      title
      variants(first: 250) {
        edges {
          node {
            id
            availableForSale
          }
        }
      }
    }
  }
`;

export default class Cart {
  storefront: StorefrontApiClient;
  cart: any | null;

  constructor() {
    const conf: any = {
      domain: 'noise-engineering.myshopify.com',
      storefrontAccessToken: '5d023b7b2d316495d543e30d6b6d8a33',
    };

    this.storefront = createStorefrontApiClient({
      storeDomain: conf.domain,
      publicAccessToken: conf.storefrontAccessToken,
      apiVersion: '2025-10',
    });
  }

  async query(query: string, variables: any) {
    const res = await this.storefront.request(query, { variables });

    //console.log(res);

    if (res.errors) {
      console.log('graphql error *******');
      if (res.errors.graphQLErrors) {
        for (const e of res.errors.graphQLErrors) {
          console.log(e.message, e.locations);
        }
        throw new Error(res.errors.graphQLErrors[0].message);
      } else {
        throw new Error(res.errors.message);
      }
    }

    return res.data;
  }

  checkErrors(data: any) {
    if (data.userErrors && data.userErrors.length) {
      for (const e of data.userErrors) {
        console.log(e);
      }
      throw new Error(data.userErrors[0]);
    }
  }

  isValid() {
    return this?.cart?.id;
  }

  async create() {
    this.cart = (await this.query(QueryCreateCart, {})).cartCreate.cart;
  }

  async load(id: string | undefined) {
    if (id) {
      this.cart = (await this.query(QueryGetCart, { Id: id })).cart;
    }
  }

  getId() {
    return this.cart.id;
  }

  getCheckoutUrl() {
    if (this.cart) {
      return this.cart.checkoutUrl;
    } else {
      return '';
    }
  }

  getLineItemCount() {
    if (this.cart) {
      return this.cart.totalQuantity;
    } else {
      return 0;
    }
  }

  getCost() {
    if (this.cart) {
      return this.cart.cost.totalAmount.amount;
    } else {
      return 0;
    }
  }

  getLineItems() {
    if (this.cart) {
      return this.cart.lines.edges;
    } else {
      return [];
    }
  }

  async addItem(merchandiseId: string, quantity: number = 1) {
    const variables = {
      cartId: this.cart.id,
      lines: [{ merchandiseId, quantity }],
    };

    const data = await this.query(QueryAddItem, variables);
    this.checkErrors(data?.cartLinesAdd);
    this.cart = data.cartLinesAdd.cart;
  }

  async remItem(variantId: string) {
    const line = this.cart.lines.edges.find(
      (edge: any) => edge.node.merchandise.id === variantId
    );

    if (!line) {
      //cant remove something that isnt there
      return;
    }

    const variables = {
      cartId: this.cart.id,
      lineIds: [line.node.id],
    };

    const data = await this.query(QueryRemItem, variables);
    this.checkErrors(data?.cartLinesRemove);
    this.cart = data.cartLinesRemove.cart;
  }

  async updateItem(variantId: string, quantity: number) {
    const line = this.cart.lines.edges.find(
      (edge: any) => edge.node.merchandise.id === variantId
    );

    if (!line) {
      await this.addItem(variantId, quantity);
      return;
    }

    const variables = {
      cartId: this.cart.id,
      lines: [{ id: line.node.id, quantity: quantity }],
    };

    const data = await this.query(QueryUpdateItem, variables);
    this.checkErrors(data?.cartLinesUpdate);
    this.cart = data.cartLinesUpdate.cart;
  }

  async setNote(note: string) {
    if (!this.cart?.id) {
      throw new Error('Cart does not exist. Cannot set note.');
    }

    const variables = {
      cartId: this.cart.id,
      attributes: [{ key: 'note', value: note }],
    };

    const data = await this.query(QuerySetNote, variables);
    this.checkErrors(data?.cartAttributesUpdate);
    this.cart = data.cartAttributesUpdate.cart;
  }

  async isAvailable(pid: string, vid: string, availDefault: boolean) {
    const prod = await this.query(QueryAvailable, { id: pid });

    if (prod?.data?.product?.variants) {
      for (const v of prod.data.product.variants.edges) {
        if (v.node.id == vid) {
          return v.node.availableForSale;
        }
      }
    }

    console.log('getProduct missing: ', prod);

    return availDefault;
  }
}

async function test() {
  var c = new Cart();
  await c.create();
  const id = c.getId();
  c.cart = undefined;
  await c.load(id);
  await c.setNote('this is a note!');
  await c.addItem('gid://shopify/ProductVariant/40298458054805');
  await c.addItem('gid://shopify/ProductVariant/44058467729656', 4);
  await c.addItem('gid://shopify/ProductVariant/44058467729656', 1);
  console.log(c.cart);

  console.log(c.getCheckoutUrl());
  console.log(c.getLineItemCount());
  console.log(JSON.stringify(c.getLineItems(), null, 2));
  console.log(c.getCost());
  console.log(c.cart);

  console.log(c.getCost());
  await c.remItem('gid://shopify/ProductVariant/44058467729656');
  console.log(c.getCost());
  await c.addItem('gid://shopify/ProductVariant/44058467729656', 4);
  console.log(c.getCost());
  await c.updateItem('gid://shopify/ProductVariant/44058467729656', 8);
  console.log(c.getCost());
}

//test();
